import { useCallback } from "react";
import {
  FilterItem,
  FilterPinItem,
} from "../FilterManagement/CommonFilterTypes";
import {
  toLegacyEntityKey,
  UNSET_FILTER_ENTITY_KEY,
  type FilterEntity,
} from "../FilterManagement/filterEntity";
import {
  filterByProperty,
  isSameRemediationItemFilterKey,
  splitFilterKey,
} from "../FilterManagement/filterKey";
import isEqual from "lodash-es/isEqual";

export const RECENTLY_USED_FILTERS_LS = "recently_used_filters";

export interface RecentlyUsedFilterOptions {
  filterEntity?: FilterEntity;
  filtersToPin?: FilterPinItem[];
}

export const MAX_RECENTLY_USED_FILTERS = 6;

const NO_PINNED_FILTERS: FilterPinItem[] = [];

const isNamespacedPinnedProperty = (
  field: string | null | undefined,
  filtersToPin: FilterPinItem[]
): boolean =>
  filterByProperty(
    filtersToPin.map(({ filterField }) => splitFilterKey(filterField)),
    splitFilterKey(field).property
  ).some(({ entity }) => !!entity);

const useRecentlyUsedFilters = ({
  filterEntity,
  filtersToPin = NO_PINNED_FILTERS,
}: RecentlyUsedFilterOptions) => {
  const lsKey = `${RECENTLY_USED_FILTERS_LS}_${
    filterEntity ? toLegacyEntityKey(filterEntity) : UNSET_FILTER_ENTITY_KEY
  }`;

  const saveNewFilterToLS = useCallback(
    (filter: FilterItem) => {
      if (isNamespacedPinnedProperty(filter.field, filtersToPin)) {
        return;
      }

      const recentlyUsedFilters = localStorage.getItem(lsKey);
      let recentlyUsedFiltersArray = [];
      try {
        const parsedFilters = recentlyUsedFilters
          ? JSON.parse(recentlyUsedFilters)
          : [];
        recentlyUsedFiltersArray = Array.isArray(parsedFilters)
          ? parsedFilters
          : [];
      } catch (e) {
        console.error("Error parsing recently used filters", e);
      }
      if (!!recentlyUsedFiltersArray.length) {
        const sameFieldFilterIndex = recentlyUsedFiltersArray.findIndex(
          (f: FilterItem) =>
            isSameRemediationItemFilterKey(f.field, filter.field)
        );
        if (sameFieldFilterIndex !== -1) {
          const isSameFilterObject = isEqual(
            recentlyUsedFiltersArray[sameFieldFilterIndex],
            filter
          );
          if (isSameFilterObject) {
            return;
          }
          recentlyUsedFiltersArray.splice(sameFieldFilterIndex, 1);
        } else {
          if (recentlyUsedFiltersArray.length >= MAX_RECENTLY_USED_FILTERS) {
            recentlyUsedFiltersArray.shift();
          }
        }
      }
      recentlyUsedFiltersArray.push(filter);

      localStorage.setItem(lsKey, JSON.stringify(recentlyUsedFiltersArray));
    },
    [lsKey, filtersToPin]
  );

  const getRecentlyUsedFilters = useCallback(() => {
    const recentlyUsedFilters = localStorage.getItem(lsKey);
    let recentlyUsedFiltersArray = [];
    try {
      const parsedFilters = recentlyUsedFilters
        ? JSON.parse(recentlyUsedFilters)
        : [];
      recentlyUsedFiltersArray = Array.isArray(parsedFilters)
        ? parsedFilters
        : [];
    } catch (e) {
      console.error("Error parsing recently used filters", e);
    }
    return recentlyUsedFiltersArray.filter(
      (filter: FilterItem) =>
        !isNamespacedPinnedProperty(filter?.field, filtersToPin)
    );
  }, [lsKey, filtersToPin]);

  return {
    saveNewFilterToLS,
    getRecentlyUsedFilters,
  };
};

export default useRecentlyUsedFilters;
